'use client';

import type { ReactElement } from 'react';
import { cn } from '#cn';
import { VStack } from './v-stack';

export type Choice<T extends string> = {
  id: T;
  title: string;
  description: string;
  icon: React.ReactNode;
};

type ChoiceListSelection<T extends string> =
  | { multiple?: false; selectedId?: T; onSelect: (id: T) => void }
  | { multiple: true; selectedIds: T[]; onToggle: (id: T) => void };

type ChoiceListProps<T extends string> = {
  choices: Choice<T>[];
  selection: ChoiceListSelection<T>;
};

/** A list of selectable option cards (icon, title, description) — single- or multi-select depending on `selection.multiple`. */
export const ChoiceList = <T extends string>({
  choices,
  selection,
}: ChoiceListProps<T>): ReactElement => {
  const isSelected = (id: T): boolean =>
    selection.multiple ? selection.selectedIds.includes(id) : selection.selectedId === id;
  const handleSelect = (id: T): void =>
    selection.multiple ? selection.onToggle(id) : selection.onSelect(id);

  return (
    <VStack as="ul" gap={4}>
      {choices.map(({ id, title, description, icon }) => {
        const selected = isSelected(id);
        return (
          <li key={id}>
            <button
              type="button"
              onClick={() => handleSelect(id)}
              aria-pressed={selected}
              className={cn(
                'w-full text-left relative rounded-xl bg-surface text-fg p-5 flex items-start gap-4',
                'transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
                selected ? 'ring-2 ring-jaaq-accent shadow-lg' : 'hover:ring-1 hover:ring-white/30',
              )}
            >
              {selected && (
                <span className="absolute left-0 top-0 bottom-0 w-1.5 bg-jaaq-accent rounded-l-xl" />
              )}
              <span className="size-12 shrink-0 rounded-lg bg-black/5 grid place-items-center text-fg">
                {icon}
              </span>
              <span className="flex-1 pt-0.5">
                <span className="block text-lg font-bold leading-6">{title}</span>
                <span className="block text-sm text-jaaq-grey-600 leading-5 mt-1">
                  {description}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </VStack>
  );
};
